// Date utility functions for MM/DD/YYYY format

export const formatDateToMMDDYYYY = (dateString: string): string => {
  if (!dateString) return '';
  
  // Handle YYYY-MM-DD format (from HTML5 date input)
  if (dateString.includes('-')) {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) {
      return dateString; // Return original if invalid date
    }
    
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const year = date.getFullYear().toString();
    
    return `${month}/${day}/${year}`;
  }
  
  // If already in MM/DD/YYYY format or unknown format, return as-is
  return dateString;
};

export const formatDateToYYYYMMDD = (dateString: string): string => {
  if (!dateString) return '';
  
  // Handle MM/DD/YYYY format
  if (dateString.includes('/')) {
    const [month, day, year] = dateString.split('/');
    const monthNum = parseInt(month);
    const dayNum = parseInt(day);
    const yearNum = parseInt(year);
    
    if (monthNum >= 1 && monthNum <= 12 && dayNum >= 1 && dayNum <= 31 && yearNum > 1900) {
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }
  }
  
  return dateString; // Return as-is if not in expected format
};

export const validateMMDDYYYYDate = (dateString: string): { isValid: boolean; error?: string } => {
  if (!dateString) {
    return { isValid: false, error: 'Date is required' };
  }
  
  // Check format MM/DD/YYYY
  const dateRegex = /^(0[1-9]|1[0-2])\/(0[1-9]|[12]\d|3[01])\/(\d{4})$/;
  if (!dateRegex.test(dateString)) {
    return { isValid: false, error: 'Please enter date in MM/DD/YYYY format' };
  }
  
  const [month, day, year] = dateString.split('/').map(num => parseInt(num));
  const date = new Date(year, month - 1, day);
  
  // Check if date is valid
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) {
    return { isValid: false, error: 'Please enter a valid date' };
  }
  
  // Check if date is not in the future
  const today = new Date();
  if (date > today) {
    return { isValid: false, error: 'Date of birth cannot be in the future' };
  }
  
  // Check age range (1-150 years)
  const age = today.getFullYear() - date.getFullYear();
  const monthDiff = today.getMonth() - date.getMonth();
  const actualAge = monthDiff < 0 || (monthDiff === 0 && today.getDate() < date.getDate()) ? age - 1 : age;
  
  if (actualAge < 1) {
    return { isValid: false, error: 'Age must be at least 1 year old' };
  }
  
  if (actualAge > 150) {
    return { isValid: false, error: 'Age cannot exceed 150 years' };
  }
  
  return { isValid: true };
};

export const formatDateInput = (value: string): string => {
  // Remove all non-digits
  const digits = value.replace(/\D/g, '');
  
  // Format as MM/DD/YYYY
  if (digits.length >= 8) {
    return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4, 8)}`;
  } else if (digits.length >= 4) {
    return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
  } else if (digits.length >= 2) {
    return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  }
  
  return digits;
};